import { Fragment, ReactNode } from 'react';

/**
 * Renders the chat answer's light markdown: paragraphs, numbered and bulleted lists, headings and
 * **bold**. The model's text only ever becomes React text nodes, never HTML, so nothing it returns
 * can inject markup. Anything else it emits is shown as plain text.
 */

type Block =
  | { kind: 'p'; text: string }
  | { kind: 'h'; text: string }
  | { kind: 'ol'; start: number; items: string[] }
  | { kind: 'ul'; items: string[] };

const ORDERED = /^\s*(\d+)[.)]\s+(.*)$/;
const UNORDERED = /^\s*[-*•]\s+(.*)$/;
const HEADING = /^\s*#{1,6}\s+(.*)$/;

export function parseAnswer(text: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];

  const flush = () => {
    if (paragraph.length) blocks.push({ kind: 'p', text: paragraph.join(' ') });
    paragraph = [];
  };

  for (const raw of text.replace(/\r\n/g, '\n').split('\n')) {
    const line = raw.trim();
    const last = blocks[blocks.length - 1];

    if (!line) {
      // A blank line ends a paragraph but not a list: models often space out numbered items.
      flush();
      continue;
    }

    const heading = HEADING.exec(raw);
    if (heading) {
      flush();
      blocks.push({ kind: 'h', text: heading[1].trim() });
      continue;
    }

    const ordered = ORDERED.exec(raw);
    if (ordered) {
      flush();
      if (last?.kind === 'ol') last.items.push(ordered[2].trim());
      else blocks.push({ kind: 'ol', start: Number(ordered[1]) || 1, items: [ordered[2].trim()] });
      continue;
    }

    const unordered = UNORDERED.exec(raw);
    if (unordered) {
      flush();
      if (last?.kind === 'ul') last.items.push(unordered[1].trim());
      else blocks.push({ kind: 'ul', items: [unordered[1].trim()] });
      continue;
    }

    // An indented line straight after a list item continues that item.
    if (!paragraph.length && /^\s+/.test(raw) && (last?.kind === 'ol' || last?.kind === 'ul')) {
      last.items[last.items.length - 1] += ` ${line}`;
      continue;
    }

    paragraph.push(line);
  }

  flush();
  return blocks;
}

function renderInline(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part, index) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4
      ? <strong key={index}>{part.slice(2, -2)}</strong>
      : <Fragment key={index}>{part}</Fragment>
  );
}

type Props = {
  text: string;
  className?: string;
};

function AiAnswer({ text, className }: Props) {
  return (
    <div className={className}>
      {parseAnswer(text).map((block, index) => {
        switch (block.kind) {
          case 'h':
            return <p key={index}><strong>{renderInline(block.text)}</strong></p>;
          case 'ol':
            return (
              <ol key={index} start={block.start}>
                {block.items.map((item, i) => <li key={i}>{renderInline(item)}</li>)}
              </ol>
            );
          case 'ul':
            return (
              <ul key={index}>
                {block.items.map((item, i) => <li key={i}>{renderInline(item)}</li>)}
              </ul>
            );
          default:
            return <p key={index}>{renderInline(block.text)}</p>;
        }
      })}
    </div>
  );
}

export default AiAnswer;
